import { categorizationOntology } from "@feed-factory/feed-factory.data.categorization-ontology";
import {
  CategorizationSource,
  CategorizationSourceBase,
  TopLevelCategorizationSource,
} from "@feed-factory/feed-factory.models.categorization-source";
import * as _ from "lodash";
import { EntityType } from "@feed-factory/feed-factory.models.ndtrc";

interface CategorizationSourceDictionary {
  [catid: string]: CategorizationSourceBase;
}

// The categorizationSourceDictionary contains ALL items, including deprecated items
// and of any entityType value. Filtering occurs in other functions.
// PRIVATE, should not be accessed by external logic
let categorizationSourceDictionary: CategorizationSourceDictionary | null =
  null;
const getCategorizationSourceDictionary =
  (): CategorizationSourceDictionary => {
    if (categorizationSourceDictionary) return categorizationSourceDictionary;

    const children = _.chain(
      categorizationOntology.categorizations as TopLevelCategorizationSource[]
    )
      .map("child")
      .flatten()
      .value() as CategorizationSource[];
    const parents = _.cloneDeep(categorizationOntology.categorizations);
    parents.forEach((parent) => {
      delete parent.child;
    });
    const flattenedCategorizationSource = (
      children as CategorizationSourceBase[]
    )
      .concat(parents as CategorizationSourceBase[])
      .filter((item) => item?.categorization);

    let catDictionary: CategorizationSourceDictionary = {};
    flattenedCategorizationSource.forEach((cat) => {
      const catClone: any = { ...cat };
      const id = catClone.cnetID;
      delete catClone.cnetID;
      catDictionary[id] = catClone;
    });

    return catDictionary;
  };

// Looks at all possible item types, including deprecated and all entityTypes
// Returns only category label
export const getCategoryById = (id: string | null): string | null =>
  getItemById(id)?.categorization || null;

// Looks at all possible item types, including deprecated and all entityTypes
// Returns all item data
export const getItemById = (
  id: string | null
): CategorizationSourceBase | null => {
  // Determine if valid id
  if (!id || id.match(/[0-9]+.[0-9]+(.[0-9]+)?/)?.[0] !== id) {
    console.error(`Requested category with invalid ID: ${id}`);
    return null;
  }

  return getCategorizationSourceDictionary()[id] || null;
};

interface SingleSelectOption {
  value: string;
  label: string;
}
interface SelectGroupedOptions {
  label: string;
  options: SingleSelectOption[];
}

// Filters on deprecated and entityType status
export const getGroupedOptions = (
  entityType: EntityType | undefined = undefined,
  showDeprecated = false
): SelectGroupedOptions[] => {
  const groupedOptions = (
    categorizationOntology.categorizations as TopLevelCategorizationSource[]
  )
    .filter((cat) => showDeprecated || !cat.deprecated)
    .filter((cat) => !cat.entityType || cat.entityType === entityType)
    .filter((topLevelCat) => !!topLevelCat.child)
    .map((topLevelCat) => {
      const transformedOptions = (topLevelCat.child as CategorizationSource[])
        .filter((cat) => showDeprecated || !cat.deprecated)
        .map((cat) => {
          return {
            value: cat.cnetID,
            label: cat.categorization,
          };
        })
        .sort((a, b) => (a.label < b.label ? -1 : 1));
      return {
        label: topLevelCat.categorization,
        options: transformedOptions,
      };
    })
    .sort((a, b) => (a.label < b.label ? -1 : 1));
  return groupedOptions;
};
